import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';
import SquarePaymentForm from '../components/SquarePaymentForm';

// Placeholder for Square payment form integration
const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APP_ID || '';
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID || '';

const Checkout: React.FC = () => {
  const { cart, clearCart } = useCart();
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const requestTokenRef = useRef<(() => Promise<string | null>) | null>(null);
  const navigate = useNavigate();

  const handlePay = async () => {
    setLoading(true);
    setMessage('');
    if (!requestTokenRef.current) {
      setMessage('Card form not ready.');
      setLoading(false);
      return;
    }
    const token = await requestTokenRef.current();
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName,
          customerEmail,
          cart: cart.map(item => ({ ...item, locationId: item.locationId })),
          paymentToken: token
        })
      });
      if (res.ok) {
        const receipt = await res.json();
        setMessage('Payment successful!');
        // Build receipt items from cart
        const items = cart.map(item => ({
          name: item.title || '',
          quantity: item.quantity || 1,
          price: item.price || 0
        }));
        const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
        const tax = subtotal * TAX_RATE;
        const total = subtotal + tax;
        clearCart();
        navigate('/receipt', {
          state: {
            receipt: {
              items,
              total,
              subtotal,
              tax,
              customerName,
              customerEmail,
              transactionId: receipt?.transactionId,
              date: receipt?.date
            }
          }
        });
      } else {
        setMessage('Payment failed.');
      }
    } catch {
      setMessage('Payment error.');
    }
    setLoading(false);
  };

  const TAX_RATE = 0.0675;
  const items = cart.map(i => ({
    id: i.id,
    title: i.title,
    qty: i.quantity ?? 1,
    price: i.price,
    subtotal: (i.quantity ?? 1) * i.price
  }));
  const subtotal = items.reduce((sum, i) => sum + i.subtotal, 0);
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax;

  return (
    <div className="min-h-screen bg-[#FDF8F5] py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-4xl font-semibold text-[#3D3D3D] mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Order Summary (read-only items) */}
          <div className="lg:col-span-2 space-y-4">
            {items.length === 0 ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-6 shadow-sm">
                <p className="font-sans text-[#3D3D3D]/70">Your cart is empty.</p>
              </motion.div>
            ) : (
              items.map((item, index) => (
                <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }} className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between flex-col sm:flex-row">
                    <div className="min-w-0">
                      <h3 className="font-serif text-lg font-semibold text-[#3D3D3D] mb-1 whitespace-normal break-words">{item.title}</h3>
                      <p className="font-sans text-sm text-[#3D3D3D]/60">Quantity: {item.qty}</p>
                    </div>
                    <div className="text-right self-end">
                      <p className="font-serif text-xl font-semibold text-[#3D3D3D]">${item.subtotal.toFixed(2)}</p>
                      {item.qty > 1 && (
                        <p className="text-sm text-[#3D3D3D]/50">${item.price.toFixed(2)} each</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          {/* Payment Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl p-8 shadow-lg sticky top-32">
              <h2 className="font-serif text-2xl font-semibold text-[#3D3D3D] mb-6">Payment Details</h2>

              <div className="space-y-3 mb-6 pb-6 border-b border-gray-100">
                <div className="flex justify-between font-sans text-[#3D3D3D]">
                  <span>Subtotal ({items.length} item{items.length > 1 ? 's' : ''})</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-sans text-[#3D3D3D]">
                  <span>Tax (6.75%)</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-between font-serif text-2xl font-semibold text-[#3D3D3D] mb-6">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="font-sans text-sm text-[#3D3D3D] mb-2 block">Your Name</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    required
                    placeholder="Jane Doe"
                    className="py-6 rounded-xl border border-gray-200 font-sans w-full"
                  />
                </div>
                <div>
                  <label className="font-sans text-sm text-[#3D3D3D] mb-2 block">Your Email</label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    required
                    placeholder="jane@example.com"
                    className="py-6 rounded-xl border border-gray-200 font-sans w-full"
                  />
                </div>

                <SquarePaymentForm
                  appId={SQUARE_APP_ID}
                  locationId={SQUARE_LOCATION_ID}
                  requestTokenRef={requestTokenRef}
                  onToken={() => {}}
                  className="space-y-2"
                />

                <button
                  className="w-full bg-[#3D3D3D] hover:bg-[#2D2D2D] text-white py-3 rounded-full font-sans text-sm leading-none"
                  disabled={!customerName.trim() || !customerEmail.trim() || cart.length === 0 || loading}
                  onClick={handlePay}
                >
                  {loading ? 'Processing...' : 'Pay Now'}
                </button>
                {message && <div className="text-[#3D3D3D] font-sans mt-3">{message}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
